import { zodResolver } from "@hookform/resolvers/zod";
import { Alert, Button, Card, CardDescription, CardTitle, Input } from "@conlit/ui";
import { BookOpen, ChevronRight } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { Link, useNavigate, useParams } from "react-router";
import { z } from "zod";
import { useAuth } from "@/features/authentication/auth-context";
import {
  joinClassroom,
  prepareClassroomJoin,
  selfRegisterClassroom,
} from "@/features/participants/participant-api";
import { rememberParticipantSession } from "@/features/participants/participant-session";
import { useLocale } from "@/features/localisation/locale-context";
import { TurnstileChallenge } from "@/components/security/turnstile";

export function LandingPage() {
  const { t } = useLocale();
  return (
    <main id="main-content">
      <section className="border-information-border bg-blue-soft border-b">
        <div className="mx-auto max-w-7xl px-6 py-20 sm:py-24">
          <p className="text-participation font-semibold">{t("home.eyebrow")}</p>
          <h1 className="text-responsive-hero font-display text-hero text-primary mt-4 max-w-4xl font-semibold">
            {t("home.title")}
          </h1>
          <p className="text-text mt-6 max-w-2xl text-lg leading-8">{t("home.description")}</p>
          <div className="mt-10 flex flex-wrap gap-4">
            <Button asChild size="lg">
              <Link to="/join">{t("home.join")}</Link>
            </Button>
            <Button asChild size="lg" variant="secondary">
              <Link to="/about">{t("home.explore")}</Link>
            </Button>
          </div>
        </div>
      </section>
      <section aria-labelledby="constitution-library" className="mx-auto max-w-7xl px-6 pt-16">
        <Card
          variant="interactive"
          className="grid items-center gap-6 md:grid-cols-[auto_1fr_auto]"
        >
          <div className="bg-gold-soft text-gold-foreground rounded-large flex size-14 items-center justify-center">
            <BookOpen aria-hidden="true" className="size-7" />
          </div>
          <div>
            <CardTitle id="constitution-library">{t("nav.constitution")}</CardTitle>
            <CardDescription className="text-base leading-7">
              {t("home.constitutionDescription")}
            </CardDescription>
          </div>
          <Button asChild variant="secondary">
            <Link to="/constitution">
              {t("home.constitutionAction")} <ChevronRight aria-hidden="true" className="size-4" />
            </Link>
          </Button>
        </Card>
      </section>
      <section aria-labelledby="audiences" className="mx-auto max-w-7xl px-6 py-16">
        <h2 id="audiences" className="text-section-title text-primary font-semibold">
          {t("home.audiences")}
        </h2>
        <div className="mt-8 grid gap-6 md:grid-cols-3">
          {[
            [t("home.students"), t("home.studentsDescription")],
            [t("home.teachers"), t("home.teachersDescription")],
            [t("home.researchers"), t("home.researchersDescription")],
          ].map(([title, description]) => (
            <Card key={title}>
              <CardTitle>{title}</CardTitle>
              <CardDescription>{description}</CardDescription>
            </Card>
          ))}
        </div>
      </section>
    </main>
  );
}
export function StaticPage({ title, children }: { title: string; children: string }) {
  return (
    <main id="main-content" className="mx-auto max-w-3xl px-6 py-16">
      <h1 className="font-display text-page-title text-primary font-semibold">{title}</h1>
      <p className="reading-width text-text mt-6 text-lg leading-8">{children}</p>
    </main>
  );
}

export function AboutPage() {
  const { t } = useLocale();
  return <StaticPage title={t("about.title")}>{t("about.description")}</StaticPage>;
}

export function PrivacyPage() {
  const { t } = useLocale();
  return <StaticPage title={t("privacy.title")}>{t("privacy.description")}</StaticPage>;
}
const joinSchema = z.object({
  sessionCode: z
    .string()
    .trim()
    .toUpperCase()
    .regex(/^[A-Z0-9]{6}$/, "Enter the six-character classroom code"),
});
type JoinInput = z.infer<typeof joinSchema>;
export function JoinPage() {
  const params = useParams();
  return params.sessionCode ? (
    <RosterCodePage sessionCode={params.sessionCode.toUpperCase()} />
  ) : (
    <ClassroomPinPage />
  );
}

function ClassroomPinPage() {
  const { t } = useLocale();
  const navigate = useNavigate();
  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<JoinInput>({
    resolver: zodResolver(joinSchema),
    defaultValues: { sessionCode: "" },
  });
  return (
    <main id="main-content" className="mx-auto max-w-xl px-6 py-16">
      <Card>
        <CardTitle>{t("join.pinTitle")}</CardTitle>
        <CardDescription>{t("join.pinDescription")}</CardDescription>
        <form
          className="mt-8 space-y-5"
          onSubmit={handleSubmit(async ({ sessionCode }) => {
            await Promise.resolve();
            navigate(`/join/${sessionCode}`);
          })}
        >
          <div>
            <label htmlFor="sessionCode" className="block font-semibold">
              {t("join.pinLabel")}
            </label>
            <Input
              id="sessionCode"
              autoComplete="off"
              inputMode="text"
              maxLength={6}
              aria-describedby="code-help code-error"
              className="mt-2 min-h-14 text-center text-2xl font-bold uppercase tracking-[0.25em]"
              {...register("sessionCode")}
            />
            <p id="code-help" className="text-small text-text-muted mt-2">
              {t("join.pinHelp")}
            </p>
            {errors.sessionCode && (
              <p id="code-error" role="alert" className="text-small text-error mt-2 font-semibold">
                {t("join.pinValidation")}
              </p>
            )}
          </div>
          <Button type="submit" className="w-full" size="lg" disabled={isSubmitting}>
            {t("join.continue")}
          </Button>
        </form>
      </Card>
    </main>
  );
}

const rosterSchema = z.object({
  rosterCode: z
    .string()
    .trim()
    .toUpperCase()
    .regex(/^[A-Z0-9][A-Z0-9 -]{5,20}$/, "Enter your recovery code"),
});
type RosterInput = z.infer<typeof rosterSchema>;
const selfRegistrationSchema = z.object({ pseudonym: z.string().trim().min(1).max(80) });
type SelfRegistrationInput = z.infer<typeof selfRegistrationSchema>;

function RosterCodePage({ sessionCode }: { sessionCode: string }) {
  const { t } = useLocale();
  const navigate = useNavigate();
  const { refreshPrincipal, user, isLoading } = useAuth();
  const [serverError, setServerError] = useState("");
  const [captchaToken, setCaptchaToken] = useState<string | null>(null);
  const [captchaReset, setCaptchaReset] = useState(0);
  const joinStarted = useRef(false);
  const [mode, setMode] = useState<"checking" | "register" | "recovery">("checking");
  const [registrationOpen, setRegistrationOpen] = useState(true);
  const [recovery, setRecovery] = useState<{
    code: string;
    classStudentId?: string;
    resumePath: string;
  }>();
  const prepareCallbacks = useRef({ navigate, refreshPrincipal, t });
  prepareCallbacks.current = { navigate, refreshPrincipal, t };
  const {
    register: registerRecovery,
    handleSubmit: handleRecoverySubmit,
    formState: { errors: recoveryErrors, isSubmitting: isRecovering },
  } = useForm<RosterInput>({ resolver: zodResolver(rosterSchema) });
  const {
    register: registerName,
    handleSubmit: handleNameSubmit,
    formState: { errors: nameErrors, isSubmitting: isRegistering },
  } = useForm<SelfRegistrationInput>({ resolver: zodResolver(selfRegistrationSchema) });

  const canPrepareJoin = !isLoading && Boolean(user || captchaToken);
  useEffect(() => {
    if (!canPrepareJoin || joinStarted.current) return;
    joinStarted.current = true;
    let active = true;
    void prepareClassroomJoin(sessionCode, captchaToken ?? undefined)
      .then(async (result) => {
        if (!active) return;
        const callbacks = prepareCallbacks.current;
        if (result.state === "joined") {
          rememberParticipantSession(result.sessionId);
          await callbacks.refreshPrincipal();
          callbacks.navigate(result.resumePath, { replace: true });
        } else if (result.state === "registration_required") {
          setRegistrationOpen(result.registrationOpen);
          setMode((current) => (current === "checking" ? "register" : current));
        } else {
          setServerError(
            result.state === "closed"
              ? callbacks.t("join.closed")
              : result.state === "different_class"
                ? callbacks.t("join.deviceLinked")
                : callbacks.t("join.genericError"),
          );
          setMode((current) => (current === "checking" ? "register" : current));
        }
      })
      .catch((error: unknown) => {
        if (!active) return;
        const callbacks = prepareCallbacks.current;
        const message = error instanceof Error ? error.message : "";
        if (message.toLowerCase().includes("captcha")) {
          joinStarted.current = false;
          setCaptchaToken(null);
          setCaptchaReset((value) => value + 1);
        }
        setServerError(
          message.includes("staff account")
            ? callbacks.t("join.staffSignedIn")
            : callbacks.t("join.genericError"),
        );
        setMode((current) => (current === "checking" ? "register" : current));
      });
    return () => {
      active = false;
      joinStarted.current = false;
    };
  }, [canPrepareJoin, captchaToken, sessionCode]);

  if (recovery) {
    return (
      <main id="main-content" className="mx-auto max-w-xl px-6 py-16">
        <Card>
          <CardTitle>{t("join.saveRecoveryTitle")}</CardTitle>
          <CardDescription>{t("join.saveRecoveryDescription")}</CardDescription>
          {recovery.classStudentId && (
            <p className="text-text-muted mt-6 text-center text-sm">
              {t("join.classStudentId", { id: recovery.classStudentId })}
            </p>
          )}
          <code className="bg-blue-soft text-primary mt-3 block rounded-lg p-5 text-center text-2xl font-bold tracking-wider">
            {recovery.code}
          </code>
          <Button
            className="mt-5 w-full"
            variant="secondary"
            onClick={() => void navigator.clipboard?.writeText(recovery.code)}
          >
            {t("join.copyRecovery")}
          </Button>
          <Button
            className="mt-3 w-full"
            size="lg"
            onClick={() =>
              void refreshPrincipal().then(() => navigate(recovery.resumePath, { replace: true }))
            }
          >
            {t("join.savedRecovery")}
          </Button>
        </Card>
      </main>
    );
  }

  return (
    <main id="main-content" className="mx-auto max-w-xl px-6 py-16">
      <Card>
        <p className="text-participation text-sm font-semibold">
          {t("join.pinValue", { pin: sessionCode })}
        </p>
        <CardTitle className="mt-2">
          {mode === "recovery" ? t("join.studentTitle") : t("join.selfRegisterTitle")}
        </CardTitle>
        <CardDescription>
          {mode === "checking"
            ? t("join.checkingDevice")
            : mode === "recovery"
              ? t("join.studentDescription")
              : t("join.selfRegisterDescription")}
        </CardDescription>
        {mode === "checking" && !user && !isLoading && (
          <div className="mt-6">
            <TurnstileChallenge
              action="student_join"
              resetKey={captchaReset}
              onToken={setCaptchaToken}
              loadingLabel={t("security.checking")}
              errorLabel={t("security.unavailable")}
            />
          </div>
        )}
        {mode === "recovery" ? (
          <form
            className="mt-8 space-y-5"
            onSubmit={handleRecoverySubmit(async ({ rosterCode }) => {
              setServerError("");
              try {
                const result = await joinClassroom(sessionCode, rosterCode);
                rememberParticipantSession(result.sessionId);
                await refreshPrincipal();
                navigate(result.resumePath, { replace: true });
              } catch (error) {
                const message = error instanceof Error ? error.message : "";
                setServerError(
                  message.includes("another student")
                    ? t("join.deviceLinked")
                    : message.includes("staff account")
                      ? t("join.staffSignedIn")
                      : t("join.invalidRecovery"),
                );
              }
            })}
          >
            <div>
              <label htmlFor="rosterCode" className="block font-semibold">
                {t("join.recoveryLabel")}
              </label>
              <Input
                id="rosterCode"
                autoComplete="off"
                className="mt-2 min-h-14 text-center text-xl font-bold uppercase tracking-[0.12em]"
                {...registerRecovery("rosterCode")}
              />
              {recoveryErrors.rosterCode && (
                <p role="alert" className="text-error mt-2 text-sm font-semibold">
                  {t("join.recoveryValidation")}
                </p>
              )}
            </div>
            {serverError && (
              <Alert variant="error" title={t("join.unable")}>
                {serverError}
              </Alert>
            )}
            <Button className="w-full" size="lg" type="submit" disabled={isRecovering}>
              {isRecovering ? t("join.joining") : t("join.recoverAction")}
            </Button>
            <Button
              className="w-full"
              variant="ghost"
              type="button"
              onClick={() => setMode("register")}
            >
              {t("join.newStudent")}
            </Button>
          </form>
        ) : (
          <form
            className="mt-8 space-y-5"
            onSubmit={handleNameSubmit(async ({ pseudonym }) => {
              setServerError("");
              try {
                const result = await selfRegisterClassroom(sessionCode, pseudonym);
                rememberParticipantSession(result.sessionId);
                if (result.recoveryCode) {
                  setRecovery({
                    code: result.recoveryCode,
                    classStudentId: result.classStudentId,
                    resumePath: result.resumePath,
                  });
                } else {
                  await refreshPrincipal();
                  navigate(result.resumePath, { replace: true });
                }
              } catch (error) {
                const message = error instanceof Error ? error.message : "";
                setServerError(
                  message.includes("STUDENT_NAME_TAKEN") || message.includes("duplicate key")
                    ? t("join.nameTaken")
                    : message.includes("REGISTRATION_LOCKED")
                      ? t("join.registrationLocked")
                      : message.includes("ANOTHER_CLASS")
                        ? t("join.deviceLinked")
                        : t("join.genericError"),
                );
              }
            })}
          >
            <div>
              <label htmlFor="pseudonym" className="block font-semibold">
                {t("join.nameLabel")}
              </label>
              <Input
                id="pseudonym"
                autoComplete="name"
                maxLength={80}
                className="mt-2 min-h-14"
                {...registerName("pseudonym")}
              />
              {nameErrors.pseudonym && (
                <p role="alert" className="text-error mt-2 text-sm font-semibold">
                  {t("join.nameValidation")}
                </p>
              )}
            </div>
            {serverError && (
              <Alert variant="error" title={t("join.unable")}>
                {serverError}
              </Alert>
            )}
            {!registrationOpen && <Alert variant="warning" title={t("join.registrationLocked")} />}
            <Button
              className="w-full"
              size="lg"
              type="submit"
              disabled={isRegistering || mode === "checking" || !registrationOpen}
            >
              {isRegistering ? t("join.joining") : t("join.action")}
            </Button>
            <Button
              className="w-full"
              variant="ghost"
              type="button"
              onClick={() => setMode("recovery")}
            >
              {t("join.useRecovery")}
            </Button>
          </form>
        )}
        <Button asChild className="mt-2 w-full" variant="ghost">
          <Link to="/join">{t("join.differentPin")}</Link>
        </Button>
      </Card>
    </main>
  );
}
export function NotFoundPage() {
  const { t } = useLocale();
  return (
    <main id="main-content" className="mx-auto max-w-3xl px-6 py-16">
      <h1 className="font-display text-page-title text-primary font-semibold">
        {t("notFound.title")}
      </h1>
      <p className="text-text-muted mt-4">{t("notFound.description")}</p>
      <Button asChild className="mt-8">
        <Link to="/">{t("error.returnHome")}</Link>
      </Button>
    </main>
  );
}
